import React, { useState, useEffect } from 'react';
import { FaTimes, FaCreditCard, FaQrcode, FaCheckCircle, FaSpinner } from 'react-icons/fa';

const TEST_CARD_NUMBER = "1111 2222 3333 4444";
const API_URL = 'http://localhost:4000/api/auth/current_user'; 

const PaymentModal = ({ carName, carPrice, carImage, carId, address, onClose, onPaymentSuccess }) => {
  const [activeTab, setActiveTab] = useState('card');
  const [cardNumber, setCardNumber] = useState('');
  const [status, setStatus] = useState('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [userId, setUserId] = useState(null); // Stores unique user ID

  // Fetch User ID on load to correctly save the order
  useEffect(() => {
    const fetchUserId = async () => {
        try {
            const response = await fetch(API_URL, { credentials: 'include' });
            const userData = await response.json();
            if (userData && userData.id) {
                setUserId(userData.id);
            } else {
                alert("Session expired. Please log in to complete payment.");
                window.location.href = '/login';
            }
        } catch (e) {
            console.error("Failed to fetch user ID for payment:", e);
            alert("Error retrieving user data. Please try logging in again.");
            window.location.href = '/login';
        }
    };
    fetchUserId();
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!userId) {
        setErrorMessage("User session failed to load. Please refresh and try again.");
        return;
    }
    
    if (activeTab === 'card' && cardNumber !== TEST_CARD_NUMBER) {
      setErrorMessage('Invalid Card Number. Use the mock card: 1111 2222 3333 4444');
      setStatus('error');
      return;
    }

    setStatus('processing');
    
    try {
      // Send confirmation to backend (Triggers delivery email with address)
      const response = await fetch('http://localhost:4000/api/booking/confirm_payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ carName, address }), // Passing address to backend
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        // 2. CRITICAL FIX: PERSIST NEW ORDER TO UNIQUE LOCAL STORAGE KEY
        try {
            // Key is unique to the user
            const storageKey = `quickWheelsOrders_${userId}`;
            const storedOrders = localStorage.getItem(storageKey);
            const orders = storedOrders ? JSON.parse(storedOrders) : [];
            
            const newOrder = {
                id: `order-${Date.now()}`, 
                carName: carName,
                price: carPrice,
                image: carImage, 
                status: "confirmed", // New orders start as confirmed
            };
            
            const updatedOrders = [newOrder, ...orders];
            localStorage.setItem(storageKey, JSON.stringify(updatedOrders));
            
        } catch (e) {
            console.error("Failed to update unique localStorage key:", e);
        }
        
        setStatus('success');
        setTimeout(() => {
          onPaymentSuccess(data.message);
          window.location.reload(); // Reloads Dashboard with new order
        }, 1000);
      } else {
        setStatus('error');
        setErrorMessage(data.message || "Payment failed. Please try again.");
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage("Network error or server connection failed.");
    }
  };

  const renderPaymentForm = () => {
    if (activeTab === 'card') {
      return (
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Card Number (Test: 1111 2222 3333 4444)"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value.replace(/\s/g, ''))}
            className="w-full p-3 rounded-lg bg-slate-700 text-white placeholder-slate-400 border border-amber-400 focus:outline-none"
            maxLength="16"
            required
          />
          <div className="grid grid-cols-3 gap-4">
            <input type="text" placeholder="MM/YY" className="col-span-1 p-3 rounded-lg bg-slate-700 text-white placeholder-slate-400" required />
            <input type="text" placeholder="CVC" className="col-span-1 p-3 rounded-lg bg-slate-700 text-white placeholder-slate-400" required />
            <input type="text" placeholder="Name on Card" className="col-span-1 p-3 rounded-lg bg-slate-700 text-white placeholder-slate-400" required />
          </div>
          <button
            type="submit"
            disabled={status === 'processing' || !userId} 
            className="w-full py-3 bg-amber-400 text-slate-950 font-bold rounded-lg hover:bg-amber-500 transition duration-300 flex items-center justify-center space-x-2"
          >
            {status === 'processing' ? (
              <>
                <FaSpinner className="animate-spin" /> <span>Processing...</span>
              </>
            ) : (
              <span>Pay ${carPrice} Now</span>
            )}
          </button>
        </form>
      );
    } else {
      return (
        <div className="text-center p-6 bg-slate-700 rounded-lg">
          <p className="text-slate-300 mb-4">Scan the QR Code to complete the payment.</p>
          <div className="w-40 h-40 bg-white mx-auto flex items-center justify-center rounded-lg">
            <FaQrcode className="text-black text-6xl" />
          </div>
          <button
            onClick={() => {
              setStatus('processing');
              setTimeout(() => handleSubmit({ preventDefault: () => {} }), 1500);
            }}
            disabled={!userId}
            className="mt-6 w-full py-3 bg-amber-400 text-slate-950 font-bold rounded-lg hover:bg-amber-500 transition duration-300"
          >
            Simulate QR Payment
          </button>
        </div>
      );
    }
  };

  const renderContent = () => {
    if (status === 'success') {
      return (
        <div className="text-center p-8">
          <FaCheckCircle className="text-green-500 text-6xl mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white">Payment Confirmed!</h2>
          <p className="text-slate-400 mt-2">Final delivery details are being sent to your email.</p>
        </div>
      );
    }
    return (
      <>
        <div className="flex justify-between items-center mb-6 border-b border-slate-700 pb-3">
          <h2 className="text-2xl font-bold text-white">Secure Payment</h2>
          <span className="text-amber-400 text-3xl font-bold">${carPrice}</span>
        </div>
        
        {errorMessage && (
          <div className="p-3 bg-red-600 text-white rounded-lg mb-4 text-sm font-medium">{errorMessage}</div>
        )}

        {/* Tab Navigation */}
        <div className="flex mb-6 space-x-4">
          <button
            onClick={() => setActiveTab('card')}
            className={`flex items-center space-x-2 py-2 px-4 rounded-lg font-semibold transition-colors ${activeTab === 'card' ? 'bg-amber-400 text-slate-950' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
          >
            <FaCreditCard /> <span>Card</span>
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`flex items-center space-x-2 py-2 px-4 rounded-lg font-semibold transition-colors ${activeTab === 'qr' ? 'bg-amber-400 text-slate-950' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
          >
            <FaQrcode /> <span>QR Code</span>
          </button>
        </div>

        {renderPaymentForm()}
      </>
    );
  };

  return (
    <div className="modal-overlay" onClick={status === 'success' ? null : onClose}>
      <div className="modal-content bg-slate-800" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="modal-close-button text-white">
          <FaTimes />
        </button>
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentModal;